import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ChannelSelectionService } from '../../../services/channel-selection-service';
import {
  Dataset,
  DocumentQueryParams,
  DocumentReader,
  FieldDataType,
  FilterGroup,
  QueryParams,
  RuleOperator,
  SchemaField,
  SystemFieldType,
  View,
  Document,
  Workflow,
  WorkflowParentType,
  ViewControlType
} from 'src/app/modules/core/store/models';
import { DataTableServerComponent } from 'src/app/modules/core/components/data-table-server/data-table.component';
import { GetRowIdParams, IServerSideDatasource, ValueGetterParams } from 'ag-grid-enterprise';
import { GridHelperService } from 'src/app/modules/core/services/grid-helper.service';
import {
  CommunicationRecipientStatus,
  ConfirmationType,
  MassNotification,
  MassNotificationRecipient,
  MassNotificationRecipientStatItem
} from '../../../store/communications';
import { CommunicationsApiService } from '../../../store/communications/communications-api.service';
import { PicklistService } from 'src/app/modules/core/services/picklist/picklist.service';
import { WorkflowApiService } from '../../../store/workflow/workflow-api.service';
import { v4 as uuidv4 } from 'uuid';
import { getCellRenderer, getValueGetter } from 'src/app/modules/core/components/ag-grid';
import { catchError, map, take, takeUntil, tap } from 'rxjs/operators';
import { Observable, Subject, of } from 'rxjs';
import { StatusPerChannelCellComponent } from './column-renderers/status-per-channel/status-per-channel-cell.component';
import { StatusByChannelCellData } from './column-renderers/status-per-channel/status-per-channel-model';
import { RecipientAddresslCellComponent } from './column-renderers/recipient-address/recipient-address-cell.component';
import { RecipientAddressCellData } from './column-renderers/recipient-address/recipient-address-model';
import { Store } from '@ngrx/store';
import * as fromComm from 'src/app/modules/communications/store/communications';
import { TextWithStatusCellData } from './column-renderers/text-with-status/text-with-status-model';
import { TextWithStatusCellComponent } from './column-renderers/text-with-status/text-with-status-cell.component';
import { ChannelUIds } from 'src/app/modules/management/store/communication-center';
import { NotificationStatusService } from '../../../services/notification-status-service';
import { CultureService } from 'src/app/modules/core/services/culture.service';
import { TemplateDesignerService } from '../../../services/template-designer.service';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import { DatasetApiService } from '@app/modules/core/services/dataset-api.service';
import { DataApiService } from '@app/modules/management/store/data/data-api.service';
import moment from 'moment';

@Component({
  selector: 'topo-comms-notification-recipients',
  templateUrl: './notification-recipients.component.html',
  styleUrls: ['./notification-recipients.component.scss'],
  standalone: false
})
export class NotificationRecipientsComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('table') dataTable: DataTableServerComponent;

  @Input() set notification(value: MassNotification) {
    this._notification = value;
    setTimeout(() => {
      this.dataTable?.groupRows(false);
    }, 100);
  }
  _notification: MassNotification;

  @Input() height: string;

  @Output() recipientClicked = new EventEmitter<MassNotificationRecipient>();

  constructor(
    private store: Store<any>,
    private translateService: TranslateService,
    private gridHelper: GridHelperService,
    private commApiService: CommunicationsApiService,
    private picklistService: PicklistService,
    private workflowApiService: WorkflowApiService,
    private channelSelectionService: ChannelSelectionService,
    private notificationStatusService: NotificationStatusService,
    private cultureService: CultureService,
    private datasetApiService: DatasetApiService,
    private dataApiService: DataApiService,
    private templateDesignerService: TemplateDesignerService,
    private changeDetector: ChangeDetectorRef
  ) {}

  datasource: any;
  columDefs: any[] = [];
  skip = 0;
  take = 10;
  totalCount: number = 0;
  searchFilterGroup: FilterGroup;
  workflows: Workflow[];
  destroy$ = new Subject<void>();
  isTableLoading: boolean;
  selectedRecipients: MassNotificationRecipient[];
  selectedStepUId: string;
  selectedStatusName: string;
  selectedStatusColorCode: string;
  workflowStatusListItems: {
    stepUId: string;
    statusName: string;
    statusHtmlColorCode: string;
  }[];
  showTableSelectedCount: boolean;
  exportDatasetUIds: string[];
  exportDatasets: Dataset[];
  datasetViewFields: { [uid: string]: { prefix: string; fields: SchemaField[] } };

  ngOnInit(): void {
    if (!!!this.channelSelectionService.availableChannels) {
      this.channelSelectionService.loadAvailableChannels(true);
    }
    this.datasource = this.getServerSideDatasource();
    this.loadWorkflowTemplates();
    this.columDefs = this.generateColumns();

    this.store
      .select(fromComm.selectIsdMassNotificationRecipientsTableChanged)
      .pipe(takeUntil(this.destroy$))
      .subscribe(isChanged => {
        if (isChanged === true) {
          this.dataTable?.refreshGrid();
          this.resetRecipientSelection();
          this.dataTable?.gridApi?.deselectAll();
        }
      });

    this.store
      .select(fromComm.selectSelectedMassNotificationStats)
      .pipe(takeUntil(this.destroy$))
      .subscribe(stats => {
        if (!!stats && this._notification?.uId === stats?.notification.uId) {
          if (!!stats?.notification) {
            this._notification = stats.notification;
            this.dataTable?.refreshGrid();
          }
        }
      });
  }

  ngAfterViewInit() {
    this.dataTable?.groupRows(false);
    this.gridHelper.clearSearchFilter();
  }

  getServerSideDatasource(): IServerSideDatasource {
    this.isTableLoading = true;
    return {
      getRows: params => {
        let sortField = 'Name';
        let sortDirection = 'Descending';
        if (params?.request.sortModel?.length > 0) {
          sortField = params?.request.sortModel[0].colId;
          sortDirection = params?.request.sortModel[0].sort === 'asc' ? 'Ascending' : 'Descending';
        }

        let translatedParams = this.gridHelper.gridParamsToRequestParams(params.request);
        const query = {
          skip: this.skip,
          take: this.take,
          sortField: sortField,
          sortDirection: sortDirection,
          where: translatedParams.where
        };

        this.commApiService.getMassNotificationRecipients(this._notification?.uId, query).subscribe(
          resp => {
            const response = this.gridHelper.queryResultToGridData(resp);
            this.totalCount = response?.rowCount;

            const rowData = response?.rowData?.map(d => {
              const data = {
                ...d,
                statusPerChannel: null,
                deliveryStatus: null,
                responseText: null,
                responseAddress: null
              };
              return data;
            });

            params.success({
              rowData: rowData,
              rowCount: response?.rowCount
            });

            this.store.dispatch(fromComm.setMassNotificationRecipientsTableChanged({ changed: false }));

            this.getNotificationRecipientStats(this._notification?.uId, rowData);

            this.changeDetector.markForCheck();
          },
          err => params.fail()
        );
      }
    };
  }

  private getRecipientStatuses(
    recipient: MassNotificationRecipient,
    allChannelConfiguredChannels: any[],
    channelItems: MassNotificationRecipientStatItem[]
  ): StatusByChannelCellData[] {
    const statuses: StatusByChannelCellData[] = [];

    let singleRecipientChannel = this.channelSelectionService.getSingleRecipientChannel(recipient);

    if (allChannelConfiguredChannels.length) {
      allChannelConfiguredChannels
        ?.filter(configuredChannel => recipient.recipientAddresses?.find(a => a.configuredChannelUId === configuredChannel.uId))
        .forEach(configuredChannel => {
          const isDM =
            (configuredChannel?.channelUId !== ChannelUIds.MicrosoftTeamsChannelUId &&
              configuredChannel?.channelUId !== ChannelUIds.SlackChannelUId) ||
            configuredChannel?.settings?.sendDirectMessages === true;

          let skip = false;
          if (!!singleRecipientChannel && configuredChannel.uId != singleRecipientChannel.uId) {
            skip = true;
          }
          if (!isDM) {
            if (!recipient.recipientAddresses?.find(a => a.configuredChannelUId === configuredChannel?.uId)) {
              skip = true;
            }
          }

          if (!skip && !!configuredChannel) {
            const items = channelItems.filter(rc => rc.configuredChannelUId == configuredChannel.uId);
            items.forEach(item => {
              let channelTitle = configuredChannel.name;
              if (!!channelTitle && !!item.address) {
                channelTitle = `${channelTitle} : ${item.address}`;
              }
              const displayStatusDetails = item.displayStatusDetails ?? null;
              const statusDetails = displayStatusDetails
                ? this.translateService.instant('COMMUNICATIONS.ERRORS.' + displayStatusDetails)
                : null;

              statuses.push({
                channelUId: configuredChannel.channelUId,
                channelIcon: this.channelSelectionService.getChannelIcon(configuredChannel.channelUId, isDM),
                configuredChannelUId: configuredChannel.uId,
                title: channelTitle,
                status: item.status ?? CommunicationRecipientStatus.None,
                statusTitle: item.displayStatusDetails ?? null,
                address: !!item.address ? item.address : '',
                statusDetails: statusDetails
              });
            });
          }
        });
    }
    return statuses;
  }

  private getNotificationRecipientStats(notificationUId: string, notificationRecipients: any[]): void {
    const uids = notificationRecipients.map(n => n.uId);

    const allChannelConfiguredChannels = this.channelSelectionService.getUniqueConfiguredChannels(
      this._notification?.channelsCascadeSettings
    );

    this.commApiService.getMassNotificationRecipientStats(notificationUId, uids, true, true).subscribe(resp => {
      resp.forEach(r => {
        const recipient = notificationRecipients.find(n => n.uId === r.massNotificationRecipientUId);
        if (!!recipient) {
          let responseColor = null;

          const channelItems = r.channelItems?.filter(i =>
            recipient.recipientAddresses?.find(a => a.configuredChannelUId === i.configuredChannelUId)
          );

          const latestResponse = r?.responses?.length > 0 ? r?.responses[0] : null;
          let replyText = latestResponse?.responseText;
          let responseText = replyText;

          if (!!latestResponse?.confirmationOptionsUId) {
            const confirmation = this._notification?.messageSettings?.confirmationButtons?.find(
              b => b.uId === latestResponse?.confirmationOptionsUId
            );
            if (!!confirmation) {
              responseColor = confirmation.type === ConfirmationType.Positive ? '#74B816' : '#E03131';
              responseText = this.templateDesignerService.getTranslateText(confirmation.text, this.cultureService.currentLang);
            }
          }
          const responseCellData: TextWithStatusCellData = {
            text: responseText,
            color: responseColor
          };

          const recipient_responseText = !!responseColor ? JSON.stringify(responseCellData) : null;

          const addressCellData: RecipientAddressCellData = {
            address: latestResponse?.responseAddress,
            channelIcon: this.channelSelectionService.getChannelIcon(latestResponse?.responseChannelUId),
            title: this.channelSelectionService.getChannelName(latestResponse?.responseChannelUId),
            communicationRecipientUId: latestResponse?.communicationRecipientUId,
            configuredChannelUId: latestResponse?.configuredChannelUId
          };

          const status = this.notificationStatusService.getBestRecipientStatus(channelItems);

          const deliveryStatusCellData: TextWithStatusCellData = {
            text: this.translateService.instant('COMMUNICATIONS.RECIPIENT-SEND-STATUS-' + status),
            title: '',
            color: this.notificationStatusService.getRecipientStatusColor(status)
          };

          const recipient_deliveryStatus = JSON.stringify(deliveryStatusCellData);

          const statuses = this.getRecipientStatuses(recipient, allChannelConfiguredChannels, channelItems);

          statuses.forEach(s => {
            s.color = this.notificationStatusService.getRecipientStatusColor(s.status);
            if (!!!s.statusTitle) {
              s.statusTitle = this.getStatusTitle(s.status);
            } else {
              const statusDetails = this.translateService.instant('COMMUNICATIONS.ERRORS.' + s.statusTitle);
              s.statusTitle = `${this.getStatusTitle(s.status)} : ${statusDetails}`;
            }
          });

          const recipient_statusPerChannel = JSON.stringify(statuses);

          const recipient_responseAddress = !!latestResponse?.responseAddress ? JSON.stringify(addressCellData) : null;

          const openedTimes = channelItems.filter(c => !!c.openedTime)?.map(c => c.openedTime);
          let openedTime = null;
          if (openedTimes?.length) {
            openedTime = this.cultureService.getDateDisplay(openedTimes[0], 'LONG-DATE-WITH-TIME');
          }

          const rowNode = this.dataTable?.gridApi?.getRowNode(recipient.uId);
          if (!!rowNode) {
            rowNode.setDataValue('_statusPerChannel', recipient_statusPerChannel);
            rowNode.setDataValue('_responseText', recipient_responseText);
            rowNode.setDataValue('_deliveryStatus', recipient_deliveryStatus);
            rowNode.setDataValue('_responseAddress', recipient_responseAddress);
            rowNode.setDataValue('_replyText', replyText);
            rowNode.setDataValue('_messageRead', openedTime);
          }
        }
      });
      this.changeDetector.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.gridHelper.clearSearchFilter();
    this.destroy$.next();
    this.destroy$.complete();
  }

  onPaginate(skip: number): void {
    this.skip = skip;
  }

  onTakeChanged(take: number): void {
    this.take = take;
  }

  onSelectData(recipients: any): void {
    this.resetRecipientSelection();

    if (recipients?.length) {
      this.selectedRecipients = recipients;
      this.getWorkflowSelectionItems();
      this.showTableSelectedCount = true;
    }
  }

  private getWorkflowSelectionItems(): void {
    const workflowActions = [];

    this.selectedRecipients.forEach(d => {
      const workflow = this.workflows.find(w => w.uId === d.workflowUId);

      const currentStep = workflow?.workflowSteps.find(step => step.uId === d.workflowStepUId);

      const actionsToAdd = currentStep.actions?.filter(a => {
        const step = workflow.workflowSteps.find(s => s.uId === a.destinationStepUId);

        if (!!workflowActions.find(s => s.stepUId === step.uId)) {
          return false;
        }

        return true;
      });

      var sortedSteps = [...workflow?.workflowSteps].sort((s1, s2) => s1.sortOrder - s2.sortOrder);
      const stepsToAdd = actionsToAdd.map(a => {
        const step = sortedSteps.find(s => s.uId === a.destinationStepUId);
        return {
          stepUId: step.uId,
          statusName: step.statusName,
          statusHtmlColorCode: step.statusHtmlColorCode
        };
      });

      workflowActions.push(...stepsToAdd);
    });

    this.workflowStatusListItems = workflowActions;
  }

  multiWorkflowMoveStepSelected(item: any): void {
    this.selectedStepUId = item.stepUId;
    this.selectedStatusName = item.statusName;
    this.selectedStatusColorCode = item.statusHtmlColorCode;
  }

  confirmMoveWorkflowResponse(response: boolean): void {
    const selectedStepUId = this.selectedStepUId;
    this.selectedStepUId = null;
    if (response) {
      const recipientUIds = this.selectedRecipients?.map(r => r.uId);
      this.store.dispatch(fromComm.transitionRecipientsToWorkflowStep({ recipientUIds: recipientUIds, targetStepUId: selectedStepUId }));
    }
  }

  private resetRecipientSelection() {
    this.selectedStepUId = null;
    this.selectedStatusName = this.translateService.instant('COMMUNICATIONS.MOVE-TO-WORKFLOW-STEP');
    this.selectedStatusColorCode = '#D5CCFF';
    this.selectedRecipients = [];
    this.showTableSelectedCount = false;
  }

  private generateColumns() {
    var columns = [];
    columns.push({
      colId: '_selectionChecks',
      checkboxSelection: true,
      headerCheckboxSelection: true,
      headerCheckboxSelectionFilteredOnly: true,
      resizable: false,
      pinned: 'left',
      width: 40,
      minWidth: 40
    });

    const nameField = {
      dataType: FieldDataType.Text,
      displayName: this.translateService.instant('COMMUNICATIONS.COLUMNS.NAME'),
      systemName: 'name',
      uId: uuidv4(),
      isUniqueKey: false,
      fieldType: SystemFieldType.System,
      picklistValues: [],
      sourceUnit: null,
      settings: null
    } as SchemaField;

    var valueGetter = getValueGetter(FieldDataType.Text);
    var cellRenderer = 'linkCellComponent';
    columns.push({
      headerName: nameField.displayName,
      field: nameField.systemName,
      colId: 'name',
      sortable: true,
      resizable: true,
      minWidth: 200,
      menuTabs: [],
      cellRenderer,
      cellRendererParams: {
        schemaField: nameField,
        picklistService: this.picklistService,
        clicked: data => {
          this.editClick(data);
        }
      },
      valueGetter
    });

    const channelStatus = {
      dataType: FieldDataType.Text,
      displayName: this.translateService.instant('COMMUNICATIONS.COLUMNS.CHANNEL-STATUS'),
      systemName: 'statusPerChannel',
      uId: uuidv4(),
      isUniqueKey: false,
      fieldType: SystemFieldType.System,
      picklistValues: [],
      sourceUnit: null
    } as SchemaField;

    valueGetter = getValueGetter(FieldDataType.Text);
    columns.push({
      headerName: channelStatus.displayName,
      field: channelStatus.systemName,
      colId: '_statusPerChannel',
      sortable: false,
      resizable: true,
      minWidth: 400,
      menuTabs: [],
      cellRenderer: StatusPerChannelCellComponent,
      cellRendererParams: {
        schemaField: channelStatus,
        statuses: [],
        picklistService: this.picklistService
      },
      valueGetter
    });

    const deliveryStatus = {
      dataType: FieldDataType.Picklist,
      displayName: this.translateService.instant('COMMUNICATIONS.COLUMNS.DELIVERY-STATUS'),
      systemName: 'deliveryStatus',
      uId: uuidv4(),
      isUniqueKey: false,
      fieldType: SystemFieldType.System,
      picklistValues: [],
      sourceUnit: null,
      settings: 'COMMUNICATIONS.COLUMNS.TYPE'
    } as SchemaField;

    valueGetter = getValueGetter(FieldDataType.Text);
    cellRenderer = 'enumTranslateCellComponent';
    columns.push({
      headerName: deliveryStatus.displayName,
      field: deliveryStatus.systemName,
      colId: '_deliveryStatus',
      sortable: false,
      resizable: true,
      minWidth: 200,
      menuTabs: [],
      cellRenderer: TextWithStatusCellComponent,
      cellRendererParams: {
        schemaField: deliveryStatus,
        picklistService: this.picklistService
      },
      valueGetter
    });

    const workflowStatus = {
      dataType: FieldDataType.UId,
      displayName: this.translateService.instant('COMMUNICATIONS.COLUMNS.RECIPIENT-STATUS'),
      systemName: 'workflowStepUId',
      uId: uuidv4(),
      isUniqueKey: false,
      fieldType: SystemFieldType.System,
      picklistValues: [],
      sourceUnit: null
    } as SchemaField;

    valueGetter = getValueGetter(FieldDataType.UId);
    cellRenderer = getCellRenderer(FieldDataType.UId);
    columns.push({
      headerName: workflowStatus.displayName,
      field: workflowStatus.systemName,
      colId: 'workflowStepUId',
      sortable: true,
      resizable: true,
      minWidth: 200,
      menuTabs: [],
      cellRenderer,
      cellRendererParams: {
        workflows: this.workflows,
        workflowFieldName: 'workflowUId',
        workflowStepFieldName: 'workflowStepUId',
        schemaField: workflowStatus,
        picklistService: this.picklistService
      },
      valueGetter
    });

    const read = {
      dataType: FieldDataType.Text,
      displayName: this.translateService.instant('COMMUNICATIONS.COLUMNS.MESSAGE-READ'),
      systemName: 'messageRead',
      uId: uuidv4(),
      isUniqueKey: false,
      fieldType: SystemFieldType.System,
      picklistValues: [],
      sourceUnit: null,
      settings: 'COMMUNICATIONS.COLUMNS.TYPE'
    } as SchemaField;

    valueGetter = getValueGetter(FieldDataType.Text);
    cellRenderer = getCellRenderer(FieldDataType.Text);
    columns.push({
      headerName: read.displayName,
      field: read.systemName,
      colId: '_messageRead',
      sortable: false,
      resizable: true,
      minWidth: 200,
      menuTabs: [],
      cellRenderer: cellRenderer,
      cellRendererParams: {
        schemaField: read,
        picklistService: this.picklistService
      },
      valueGetter
    });

    const response = {
      dataType: FieldDataType.Text,
      displayName: this.translateService.instant('COMMUNICATIONS.COLUMNS.RESPONSE'),
      systemName: 'responseText',
      uId: uuidv4(),
      isUniqueKey: false,
      fieldType: SystemFieldType.System,
      picklistValues: [],
      sourceUnit: null,
      settings: 'COMMUNICATIONS.COLUMNS.TYPE'
    } as SchemaField;

    valueGetter = getValueGetter(FieldDataType.Text);
    columns.push({
      headerName: response.displayName,
      field: response.systemName,
      colId: '_responseText',
      sortable: false,
      resizable: true,
      minWidth: 250,
      menuTabs: [],
      cellRenderer: TextWithStatusCellComponent,
      cellRendererParams: {
        schemaField: response,
        picklistService: this.picklistService
      },
      valueGetter
    });

    const reply = {
      dataType: FieldDataType.Text,
      displayName: this.translateService.instant('COMMUNICATIONS.COLUMNS.REPLY'),
      systemName: 'replyText',
      uId: uuidv4(),
      isUniqueKey: false,
      fieldType: SystemFieldType.System,
      picklistValues: [],
      sourceUnit: null,
      settings: 'COMMUNICATIONS.COLUMNS.TYPE'
    } as SchemaField;

    valueGetter = getValueGetter(FieldDataType.Text);
    cellRenderer = getCellRenderer(FieldDataType.Text);
    columns.push({
      headerName: reply.displayName,
      field: reply.systemName,
      colId: '_replyText',
      sortable: false,
      resizable: true,
      minWidth: 250,
      menuTabs: [],
      cellRenderer: cellRenderer,
      cellRendererParams: {
        schemaField: reply,
        picklistService: this.picklistService
      },
      valueGetter
    });

    const respondedOn = {
      dataType: FieldDataType.Text,
      displayName: this.translateService.instant('COMMUNICATIONS.COLUMNS.RESPONDED-ON'),
      systemName: 'responseAddress',
      uId: uuidv4(),
      isUniqueKey: false,
      fieldType: SystemFieldType.System,
      picklistValues: [],
      sourceUnit: null,
      settings: null
    } as SchemaField;

    var valueGetter = getValueGetter(FieldDataType.Text);
    columns.push({
      headerName: respondedOn.displayName,
      field: respondedOn.systemName,
      colId: '_responseAddress',
      sortable: false,
      resizable: true,
      minWidth: 400,
      menuTabs: [],
      cellRenderer: RecipientAddresslCellComponent,
      cellRendererParams: {
        schemaField: respondedOn,
        picklistService: this.picklistService
      },
      valueGetter
    });

    return columns;
  }

  private getStatusTitle(status: CommunicationRecipientStatus): string {
    return !!status || status === 0 ? this.translateService.instant(`MAP.COMMUNICATION-RECIPIENT-STATUS-${status}`) : '';
  }

  getDeliveryStatusValue = (params: ValueGetterParams) => {
    const field = params.colDef.cellRendererParams.schemaField;

    const reader = new DocumentReader(params.data);
    const value = reader.getFieldValue(field.systemName);

    return this.getStatusTitle(value);
  };

  private getRecipientActions(notification: MassNotification): any[] {
    const actions = [];
    return actions;
  }

  onSearchFilterChanged(filterGroup: FilterGroup): void {
    this.searchFilterGroup = filterGroup;
  }

  onGridDataLoaded(): void {
    this.isTableLoading = false;
  }

  editClick(recipient: MassNotificationRecipient) {
    this.recipientClicked.emit(recipient);
  }

  deleteClick(recipient: MassNotificationRecipient) {}

  goToStepClick(recipient: MassNotificationRecipient, destinationStepUId: string) {
    const updatedRecipient = {
      ...recipient,
      workflowStepUId: destinationStepUId
    };

    this.store.dispatch(fromComm.updateMassNotificationRecipient({ recipient: updatedRecipient, close: true }));
  }

  private loadWorkflowTemplates(): void {
    this.getWorkflowTemplates()
      .pipe(take(1))
      .subscribe(data => {
        this.workflows = data;
        if (!!this.workflows) {
          this.columDefs = this.generateColumns();
        }
      });
  }

  private getWorkflowTemplates(): Observable<Workflow[]> {
    const params: QueryParams = {
      skip: 0,
      take: 1000,
      where: `IsActive=true && (ParentType=${WorkflowParentType.MassNotificationRecipient})`,
      sortField: 'Name',
      sortDirection: 'Ascending'
    };
    return this.workflowApiService.getWorkflows(params, true).pipe(
      catchError(() => of({ data: [] })),
      map(({ data }) => {
        return data;
      })
    );
  }

  getRowId = (params: GetRowIdParams): string => {
    return params.data.uId;
  };

  // export
  onExportToExcel(columnNames?: string[]): void {
    this.exportDatasets = [];
    this.commApiService.getMassNotificationRecipientDatasetUIds(this._notification?.uId).subscribe(res => {
      this.exportDatasetUIds = res;
      this.datasetViewFields = {};

      if (this.exportDatasetUIds?.length > 0) {
        const queryParams: QueryParams = {
          skip: 0,
          take: this.exportDatasetUIds.length,
          where: null,
          sortField: 'name',
          sortDirection: 'Ascending'
        };

        this.datasetApiService.getDatasets(queryParams, true, true, this.exportDatasetUIds).subscribe(ds => {
          this.exportDatasets = ds?.data;
          this.populateDatasetViewFields();
          this.exportToExcelFromClient();
        });
      } else {
        this.exportToExcelFromClient();
      }
    });
  }

  private populateDatasetViewFields(): void {
    this.datasetViewFields = {};

    this.exportDatasets?.forEach(dataset => {
      let editView = dataset?.workflow?.views?.find(v => v.name === 'detail');
      let viewContainers = [];
      if (editView.controls?.some(c => c.controlType === ViewControlType.ViewControlContainer)) {
        viewContainers = editView.controls?.filter(c => c.controlType === ViewControlType.ViewControlContainer);
      } else {
        viewContainers = [editView];
      }

      this.datasetViewFields[dataset.uId] = { prefix: '', fields: [] };

      viewContainers.forEach(editView => {
        editView.controls
          .filter(c => !!c.dataSetFieldUId && dataset.schema.fields.some(f => f.uId === c.dataSetFieldUId))
          .forEach(c => {
            const field = dataset.schema.fields.find(f => f.uId === c.dataSetFieldUId);
            if (!!field && !this.datasetViewFields[dataset.uId].fields.some(f => f.systemName === field.systemName))
              if (
                field.dataType !== FieldDataType.Geometry &&
                field.dataType !== FieldDataType.Address &&
                field.dataType !== FieldDataType.UId &&
                field.dataType !== FieldDataType.User
              ) {
                this.datasetViewFields[dataset.uId].fields.push(field);
              }
          });
      });
    });
  }

  private exportContext: {
    query: any;
    totalCount: number;
    sheets: { [sheet: string]: { resp: any[]; rows: any[]; columnSettings: any[] } };
  };

  private exportToExcelFromClient() {
    this.exportContext = {
      sheets: {},
      query: {
        skip: 0,
        take: 500,
        sortField: 'name',
        sortDirection: 'Ascending',
        where: null
      },
      totalCount: 100000
    };

    const headerRows = this.generateRecipientsExcelSheetHeader();
    this.exportContext.sheets['Recipients'] = {
      rows: [],
      resp: [],
      columnSettings: []
    };
    headerRows.forEach(c => {
      this.exportContext.sheets['Recipients'].columnSettings.push({
        wpx: 175
      });
    });
    this.exportContext.sheets['Recipients'].rows.push(headerRows);

    Object.keys(this.datasetViewFields).forEach(d => {
      const columns = [];
      columns.push('Recipient');
      this.exportContext.sheets[d] = { rows: [], resp: [], columnSettings: [] };
      this.datasetViewFields[d].fields.forEach(f => {
        columns.push(f.displayName);
      });
      columns.forEach(c => {
        this.exportContext.sheets[d].columnSettings.push({
          wpx: 175
        });
      });

      this.exportContext.sheets[d].rows.push(columns);
    });

    this.exportToExcelFromClientBatch();
  }

  private exportToExcelFromClientBatch() {
    if (this.exportContext.totalCount > this.exportContext.query.skip) {
      this.commApiService.getMassNotificationRecipients(this._notification?.uId, this.exportContext.query).subscribe(res => {
        this.exportContext.totalCount = res.totalCount;

        // collect all the documents per dataset that are needed for the export
        const dsDocs: { dsUId: string; docUIds: string[]; documents: Document[] }[] = [];
        res.data?.forEach(r => {
          if (!!r.sourceDatasetUId && !!r.sourceDocumentUId && this.exportDatasets?.some(d => d.uId === r.sourceDatasetUId)) {
            let dsDoc = dsDocs.find(d => d.dsUId === r.sourceDatasetUId);
            if (!!!dsDoc) {
              dsDoc = { dsUId: r.sourceDatasetUId, docUIds: [], documents: [] };
              dsDocs.push(dsDoc);
            }
            dsDoc.docUIds.push(r.sourceDocumentUId);
          }
        });

        const docsToProcess = dsDocs.filter(dsDoc => dsDoc.docUIds.length);

        if (docsToProcess.length) {
          // get documents that are needed for the export
          docsToProcess.forEach((dsDoc, index) => {
            const query = {
              filterGroup: {
                filters: []
              },
              pagination: {
                skip: 0,
                take: dsDoc.docUIds.length
              }
            } as DocumentQueryParams;

            query.filterGroup.filters.push({
              field: 'uid',
              operator: RuleOperator.In,
              value: dsDoc.docUIds.join(',')
            });

            this.dataApiService.getDocuments(dsDoc.dsUId, query).subscribe(docs => {
              dsDoc.documents = docs.data;
              if (index == docsToProcess.length - 1) {
                this.populateExportRecipientData(res.data, dsDocs);
              }
            });
          });
        } else {
          this.populateExportRecipientData(res.data, []);
        }
      });
    } else {
      /* generate workbook and add the worksheets */
      const wb: XLSX.WorkBook = XLSX.utils.book_new();

      Object.keys(this.exportContext.sheets).forEach(s => {
        const ws: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(this.exportContext.sheets[s].rows);
        ws['!cols'] = this.exportContext.sheets[s].columnSettings;

        if (s === 'Recipients') {
          XLSX.utils.book_append_sheet(wb, ws, 'Recipients');
        } else {
          const dataset = this.exportDatasets?.find(d => d.uId === s);
          if (dataset != null) {
            const sheetName = dataset.name.length > 30 ? dataset.name.substring(0, 30) : dataset.name;
            XLSX.utils.book_append_sheet(wb, ws, sheetName);
          }
        }
      });

      /* save to file */
      XLSX.writeFile(wb, `${this._notification.name}_RecipientsExport.xlsx`);
    }
  }

  private populateExportRecipientData(
    recipients: MassNotificationRecipient[],
    dsDocs: { dsUId: string; docUIds: string[]; documents: Document[] }[]
  ): void {
    const allChannelConfiguredChannels = this.channelSelectionService.getUniqueConfiguredChannels(
      this._notification?.channelsCascadeSettings
    );

    const uids = recipients.map(n => n.uId);
    this.commApiService.getMassNotificationRecipientStats(this._notification?.uId, uids, true, true).subscribe(sres => {
      sres.forEach(r => {
        const recipient = recipients.find(n => n.uId === r.massNotificationRecipientUId);
        if (!!recipient) {
          const channelItems = r.channelItems?.filter(i =>
            recipient.recipientAddresses?.find(a => a.configuredChannelUId === i.configuredChannelUId)
          );
          let statuses = this.getRecipientStatuses(recipient, allChannelConfiguredChannels, channelItems);

          if (!(statuses?.length > 0)) {
            statuses = [];
            allChannelConfiguredChannels?.forEach(c => {
              statuses.push({
                status: CommunicationRecipientStatus.None,
                configuredChannelUId: c.uId,
                channelUId: c.channelUId,
                title: c.name
              });
            });
          }

          statuses.forEach(s => {
            const data = [];

            // comm name
            data.push(this._notification.name);

            // recipient name
            data.push(recipient.name);

            // channel type
            const configuredChannel = allChannelConfiguredChannels.find(c => c.uId === s.configuredChannelUId);
            data.push(configuredChannel?.name || '');

            // address
            data.push(s.address || '');

            // channel status
            data.push(this.getStatusTitle(s.status));

            // delivery status
            const channelItem = r.channelItems.find(c => c.configuredChannelUId === s.configuredChannelUId);
            const status = channelItem?.status ?? CommunicationRecipientStatus.None;
            data.push(this.translateService.instant('COMMUNICATIONS.RECIPIENT-SEND-STATUS-' + status));

            // status details
            data.push(s.statusDetails || '');

            const workflow = this.workflows.find(w => w.uId === recipient.workflowUId);
            const currentStep = workflow?.workflowSteps.find(step => step.uId === recipient.workflowStepUId);

            // recipient status
            data.push(currentStep?.name || '');

            let response = r?.responses.find(r => r.responseConfiguredChannelUId === s.configuredChannelUId);

            let replyText = response?.responseText;
            let responseText = replyText;

            if (!!response?.confirmationOptionsUId) {
              const confirmation = this._notification?.messageSettings?.confirmationButtons?.find(
                b => b.uId === response?.confirmationOptionsUId
              );
              if (!!confirmation) {
                responseText = this.templateDesignerService.getTranslateText(confirmation.text, this.cultureService.currentLang);
              }
            }

            // response text
            data.push(responseText || '');

            // replay text
            data.push(replyText || '');

            // response address
            data.push(response?.responseAddress || '');

            // response time
            const responseTime = !!response?.responseTime ? moment(response.responseTime).format('YYYY-MM-DD HH:mm') : '';
            data.push(responseTime);

            this.exportContext.sheets['Recipients'].rows.push(data);

            // check if need to add to a dataset sheet
            if (!!recipient.sourceDatasetUId && !!recipient.sourceDocumentUId && !!this.exportContext.sheets[recipient.sourceDatasetUId]) {
              if (!this.exportContext.sheets[recipient.sourceDatasetUId].resp.includes(recipient.uId)) {
                this.exportContext.sheets[recipient.sourceDatasetUId].resp.push(recipient.uId);
                const dsData = [];
                if (!!s.address) {
                  dsData.push(s.address);
                } else {
                  dsData.push(recipient.name);
                }
                const fieldsData = this.datasetViewFields[recipient.sourceDatasetUId];
                const dsDoc = dsDocs.find(ds => ds.dsUId === recipient.sourceDatasetUId);
                if (!!dsDoc) {
                  const doc = dsDoc.documents.find(d => d['uid'] === recipient.sourceDocumentUId);
                  if (!!doc) {
                    fieldsData.fields.forEach(f => {
                      if (f.dataType == FieldDataType.DateTime && !!doc[f.systemName]) {
                        dsData.push(this.cultureService.getDateDisplay(doc[f.systemName], 'LONG-DATE-WITH-TIME'));
                      } else {
                        dsData.push(doc[f.systemName]);
                      }
                    });
                  }
                }
                this.exportContext.sheets[recipient.sourceDatasetUId].rows.push(dsData);
              }
            }
          });
        }
      });

      this.exportContext.query.skip += this.exportContext.query.take;
      this.exportToExcelFromClientBatch();
    });
  }

  private generateRecipientsExcelSheetHeader(): any[] {
    const columns = [
      this.translateService.instant('COMMUNICATIONS.COLUMNS.COMMUNICATION-NAME'),
      this.translateService.instant('COMMUNICATIONS.COLUMNS.RECIPIENT-NAME'),
      this.translateService.instant('COMMUNICATIONS.COLUMNS.CHANNEL-TYPE'),
      this.translateService.instant('COMMUNICATIONS.COLUMNS.ADDRESS'),
      this.translateService.instant('COMMUNICATIONS.COLUMNS.CHANNEL-STATUS'),
      this.translateService.instant('COMMUNICATIONS.COLUMNS.DELIVERY-STATUS'),
      this.translateService.instant('COMMUNICATIONS.COLUMNS.STATUS-DETAILS'),
      this.translateService.instant('COMMUNICATIONS.COLUMNS.RECIPIENT-STATUS'),
      this.translateService.instant('COMMUNICATIONS.COLUMNS.RESPONSE'),
      this.translateService.instant('COMMUNICATIONS.COLUMNS.REPLY'),
      this.translateService.instant('COMMUNICATIONS.COLUMNS.RESPONDED-ON'),
      this.translateService.instant('COMMUNICATIONS.COLUMNS.RESPONSE-TIME')
    ];

    return columns;
  }
}
